import { useEffect, useMemo, useState, useCallback } from "react";
import { api, brl } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Plus, Landmark, Wallet, CreditCard, Coins, Building2, Eye,
  Power, PowerOff, PencilLine, RefreshCw, Search,
} from "lucide-react";
import { toast } from "sonner";

const TIPOS = [
  { key: "caixa", label: "Caixa", icon: Coins },
  { key: "conta_bancaria", label: "Conta bancária", icon: Landmark },
  { key: "conta_pagamento", label: "Conta pagamento", icon: CreditCard },
  { key: "carteira", label: "Carteira digital", icon: Wallet },
  { key: "outro", label: "Outro", icon: Wallet },
];
const TIPO_LABEL = Object.fromEntries(TIPOS.map((t) => [t.key, t.label]));
const TIPO_ICON = Object.fromEntries(TIPOS.map((t) => [t.key, t.icon]));

const emptyForm = () => ({
  nome: "", tipo: "conta_bancaria",
  banco: "", agencia: "", conta: "", digito_conta: "",
  titular: "", documento_titular: "",
  pousada_id: "", saldo_inicial: "0",
  moeda: "BRL", observacoes: "",
});

function StatusBadge({ ativo }) {
  return (
    <span
      data-testid={ativo ? "cf-status-ativa" : "cf-status-inativa"}
      className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold ${
        ativo
          ? "bg-[#EAF0EA] text-[#083A6B] border border-[#083A6B]/20"
          : "bg-stone-100 text-stone-500 border border-stone-300"
      }`}
    >
      {ativo ? "Ativa" : "Inativa"}
    </span>
  );
}

function TipoTag({ tipo }) {
  const Icon = TIPO_ICON[tipo] || Wallet;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-stone-700">
      <Icon size={14} strokeWidth={1.6} className="text-[#083A6B]" />
      {TIPO_LABEL[tipo] || tipo}
    </span>
  );
}

export default function ContasFinanceiras() {
  const [items, setItems] = useState([]);
  const [pousadas, setPousadas] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erroCarga, setErroCarga] = useState(null);
  const [busca, setBusca] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("all");
  const [filtroPousada, setFiltroPousada] = useState("all");
  const [filtroStatus, setFiltroStatus] = useState("all"); // all|ativa|inativa

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const [detalheAlvo, setDetalheAlvo] = useState(null);
  const [detalheOpen, setDetalheOpen] = useState(false);
  const [detalheLoading, setDetalheLoading] = useState(false);

  const [confirmAlvo, setConfirmAlvo] = useState(null);
  const [toggling, setToggling] = useState(false);

  const podeEscrever = !!(me && (me.is_admin || me.is_finance));

  const load = useCallback(async () => {
    setLoading(true); setErroCarga(null);
    try {
      const params = {};
      if (filtroTipo !== "all") params.tipo = filtroTipo;
      if (filtroPousada !== "all") params.pousada_id = filtroPousada;
      if (filtroStatus !== "all") params.ativo = filtroStatus === "ativa";
      const { data } = await api.get("/contas-financeiras", { params });
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setItems([]);
      setErroCarga(err?.response?.status === 401 ? "Sessão expirada. Faça login novamente." : "Não foi possível carregar as contas financeiras.");
    } finally {
      setLoading(false);
    }
  }, [filtroTipo, filtroPousada, filtroStatus]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: p }, { data: u }] = await Promise.all([
          api.get("/pousadas-list").catch(() => ({ data: [] })),
          api.get("/auth/me").catch(() => ({ data: null })),
        ]);
        setPousadas(Array.isArray(p) ? p.filter((x) => x.id) : []);
        setMe(u);
      } catch {
        setPousadas([]);
      }
    })();
  }, []);

  const pousadaNome = useMemo(() => {
    const m = {};
    pousadas.forEach((p) => { m[p.id] = p.nome; });
    return m;
  }, [pousadas]);

  const itensFiltradosBusca = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return items;
    return items.filter((c) => {
      const alvo = [c.nome, c.banco, c.titular, TIPO_LABEL[c.tipo], pousadaNome[c.pousada_id]]
        .filter(Boolean).join(" ").toLowerCase();
      return alvo.includes(q);
    });
  }, [items, busca, pousadaNome]);

  const abrirNovo = () => {
    setEditingId(null); setForm(emptyForm()); setFormOpen(true);
  };

  const abrirEdicao = (c) => {
    setEditingId(c.id);
    setForm({
      nome: c.nome || "",
      tipo: c.tipo || "conta_bancaria",  // não é editável no backend
      banco: c.banco || "", agencia: c.agencia || "",
      conta: c.conta || "", digito_conta: c.digito_conta || "",
      titular: c.titular || "", documento_titular: c.documento_titular || "",
      pousada_id: c.pousada_id || "",
      saldo_inicial: String(c.saldo_inicial ?? "0"),
      moeda: c.moeda || "BRL",
      observacoes: c.observacoes || "",
    });
    setFormOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim()) return toast.error("Informe o nome da conta");
    if (!form.tipo) return toast.error("Selecione o tipo da conta");
    const payload = {
      nome: form.nome.trim(),
      banco: form.banco.trim(), agencia: form.agencia.trim(),
      conta: form.conta.trim(), digito_conta: form.digito_conta.trim(),
      titular: form.titular.trim(), documento_titular: form.documento_titular.trim(),
      pousada_id: form.pousada_id || null,
      saldo_inicial: Number(form.saldo_inicial || 0),
      moeda: form.moeda || "BRL",
      observacoes: form.observacoes.trim(),
    };
    setSaving(true);
    try {
      if (editingId) {
        await api.patch(`/contas-financeiras/${editingId}`, payload);
        toast.success("Conta atualizada");
      } else {
        await api.post("/contas-financeiras", { ...payload, tipo: form.tipo });
        toast.success("Conta cadastrada");
      }
      setFormOpen(false);
      setEditingId(null);
      setForm(emptyForm());
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erro ao salvar. Verifique os dados.";
      toast.error(String(msg));
    } finally {
      setSaving(false);
    }
  };

  const abrirDetalhe = async (c) => {
    setDetalheAlvo(c); setDetalheOpen(true); setDetalheLoading(true);
    try {
      const { data } = await api.get(`/contas-financeiras/${c.id}`);
      setDetalheAlvo(data);
    } catch {
      // mantém o alvo básico da lista
    } finally {
      setDetalheLoading(false);
    }
  };

  const confirmarToggle = (c) => setConfirmAlvo(c);

  const executarToggle = async () => {
    if (!confirmAlvo) return;
    const acao = confirmAlvo.ativo ? "desativar" : "ativar";
    setToggling(true);
    try {
      await api.post(`/contas-financeiras/${confirmAlvo.id}/${acao}`);
      toast.success(acao === "desativar" ? "Conta desativada" : "Conta ativada");
      setConfirmAlvo(null);
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || `Erro ao ${acao} a conta`;
      toast.error(String(msg));
    } finally {
      setToggling(false);
    }
  };

  return (
    <div data-testid="page-contas-financeiras">
      <PageHeader
        eyebrow="Financeiro · Bancos e Contas"
        title="Contas Financeiras"
        description="Gerencie caixas, contas bancárias, contas de pagamento e carteiras utilizadas pelo grupo."
        actions={
          podeEscrever ? (
            <Dialog open={formOpen} onOpenChange={(v) => { setFormOpen(v); if (!v) { setEditingId(null); setForm(emptyForm()); } }}>
              <DialogTrigger asChild>
                <Button data-testid="btn-nova-conta" onClick={abrirNovo}>
                  <Plus size={16} /> Nova conta
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{editingId ? "Editar conta financeira" : "Nova conta financeira"}</DialogTitle>
                  <DialogDescription>
                    Preencha os dados da conta. Campos sensíveis serão mascarados para perfis de leitura.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label htmlFor="cf-nome">Nome *</Label>
                    <Input id="cf-nome" data-testid="input-cf-nome" value={form.nome}
                      onChange={(e) => setForm({ ...form, nome: e.target.value })}
                      placeholder="Ex.: Caixa Sede, Itaú Corporativo, Nubank Guacurí" required />
                  </div>
                  <div>
                    <Label htmlFor="cf-tipo">Tipo *</Label>
                    <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })} disabled={!!editingId}>
                      <SelectTrigger id="cf-tipo" data-testid="select-cf-tipo">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        {TIPOS.map((t) => (
                          <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {editingId && (
                      <p className="text-[11px] text-stone-500 mt-1">Tipo não pode ser alterado após o cadastro.</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="cf-moeda">Moeda</Label>
                    <Input id="cf-moeda" value={form.moeda} readOnly disabled />
                  </div>
                  <div>
                    <Label htmlFor="cf-banco">Banco</Label>
                    <Input id="cf-banco" data-testid="input-cf-banco" value={form.banco}
                      onChange={(e) => setForm({ ...form, banco: e.target.value })} placeholder="Ex.: Itaú, Nubank" />
                  </div>
                  <div>
                    <Label htmlFor="cf-agencia">Agência</Label>
                    <Input id="cf-agencia" data-testid="input-cf-agencia" value={form.agencia}
                      onChange={(e) => setForm({ ...form, agencia: e.target.value })} placeholder="0000" />
                  </div>
                  <div>
                    <Label htmlFor="cf-conta">Conta</Label>
                    <Input id="cf-conta" data-testid="input-cf-conta" value={form.conta}
                      onChange={(e) => setForm({ ...form, conta: e.target.value })} placeholder="00000" />
                  </div>
                  <div>
                    <Label htmlFor="cf-digito">Dígito</Label>
                    <Input id="cf-digito" data-testid="input-cf-digito" value={form.digito_conta}
                      onChange={(e) => setForm({ ...form, digito_conta: e.target.value })} placeholder="0" />
                  </div>
                  <div>
                    <Label htmlFor="cf-titular">Titular</Label>
                    <Input id="cf-titular" data-testid="input-cf-titular" value={form.titular}
                      onChange={(e) => setForm({ ...form, titular: e.target.value })} placeholder="Nome do titular" />
                  </div>
                  <div>
                    <Label htmlFor="cf-doc">CPF/CNPJ do titular</Label>
                    <Input id="cf-doc" data-testid="input-cf-documento" value={form.documento_titular}
                      onChange={(e) => setForm({ ...form, documento_titular: e.target.value })} placeholder="Somente números" />
                  </div>
                  <div>
                    <Label htmlFor="cf-pousada">Pousada</Label>
                    <Select
                      value={form.pousada_id || "none"}
                      onValueChange={(v) => setForm({ ...form, pousada_id: v === "none" ? "" : v })}
                    >
                      <SelectTrigger id="cf-pousada" data-testid="select-cf-pousada">
                        <SelectValue placeholder="Sem pousada / Corporativo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sem pousada / Corporativo</SelectItem>
                        {pousadas.map((p) => (
                          <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="cf-saldo">Saldo inicial (informativo)</Label>
                    <Input id="cf-saldo" data-testid="input-cf-saldo" type="number" step="0.01"
                      value={form.saldo_inicial}
                      onChange={(e) => setForm({ ...form, saldo_inicial: e.target.value })} />
                    <p className="text-[11px] text-stone-500 mt-1">
                      Cadastro apenas. Saldo disponível será calculado por Movimentos Financeiros (F6).
                    </p>
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="cf-obs">Observações</Label>
                    <Input id="cf-obs" data-testid="input-cf-observacoes" value={form.observacoes}
                      onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
                  </div>
                  <DialogFooter className="md:col-span-2">
                    <Button type="button" variant="outline"
                      onClick={() => { setFormOpen(false); setEditingId(null); setForm(emptyForm()); }}>
                      Cancelar
                    </Button>
                    <Button type="submit" data-testid="btn-salvar-cf" disabled={saving}>
                      {saving ? "Salvando..." : editingId ? "Salvar alterações" : "Cadastrar conta"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          ) : null
        }
      />

      {/* Filtros */}
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div className="flex-1 min-w-[220px]">
          <Label htmlFor="cf-busca" className="text-[11px] uppercase tracking-widest text-stone-500">Busca</Label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input id="cf-busca" data-testid="input-cf-busca" value={busca}
              onChange={(e) => setBusca(e.target.value)} placeholder="Nome, banco, titular..."
              className="pl-8" />
          </div>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Tipo</Label>
          <Select value={filtroTipo} onValueChange={setFiltroTipo}>
            <SelectTrigger data-testid="select-filtro-tipo" className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os tipos</SelectItem>
              {TIPOS.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Pousada</Label>
          <Select value={filtroPousada} onValueChange={setFiltroPousada}>
            <SelectTrigger data-testid="select-filtro-pousada" className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {pousadas.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Status</Label>
          <Select value={filtroStatus} onValueChange={setFiltroStatus}>
            <SelectTrigger data-testid="select-filtro-status" className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="ativa">Ativa</SelectItem>
              <SelectItem value="inativa">Inativa</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="sm" onClick={load} aria-label="Atualizar lista" data-testid="btn-refresh-cf">
          <RefreshCw size={14} />
        </Button>
      </div>

      {/* Conteúdo */}
      {loading ? (
        <div data-testid="cf-loading" className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-10 text-center text-stone-500">
          Carregando contas...
        </div>
      ) : erroCarga ? (
        <div data-testid="cf-erro" className="bg-[#FFFFFF] border border-[#D4A652]/30 rounded-lg p-10 text-center text-[#D4A652]">
          {erroCarga}
        </div>
      ) : itensFiltradosBusca.length === 0 ? (
        <div data-testid="cf-empty" className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-12 text-center">
          <div className="w-14 h-14 rounded-full bg-[#EAF0EA] flex items-center justify-center mx-auto mb-4 text-[#083A6B]">
            <Landmark size={22} strokeWidth={1.5} />
          </div>
          <div className="font-serif text-2xl text-stone-900 mb-2">Nenhuma conta financeira cadastrada.</div>
          <p className="text-sm text-stone-600 mb-6 max-w-md mx-auto">
            Cadastre a primeira conta para começar a acompanhar caixas, bancos e carteiras da rede.
          </p>
          {podeEscrever && (
            <Button data-testid="btn-empty-cadastrar" onClick={abrirNovo}>
              <Plus size={16} /> Cadastrar primeira conta
            </Button>
          )}
        </div>
      ) : (
        <>
          {/* Mobile: cards */}
          <div className="md:hidden space-y-3">
            {itensFiltradosBusca.map((c) => (
              <div key={c.id} data-testid={`cf-card-${c.id}`}
                className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-4">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <div className="font-serif text-lg text-stone-900 truncate">{c.nome}</div>
                    <TipoTag tipo={c.tipo} />
                  </div>
                  <StatusBadge ativo={c.ativo} />
                </div>
                <div className="text-xs text-stone-600 space-y-1 mb-3">
                  {c.banco && <div><b>Banco:</b> {c.banco}</div>}
                  {c.conta && <div><b>Conta:</b> {c.conta}{c.digito_conta ? `-${c.digito_conta}` : ""}</div>}
                  {c.pousada_id && <div><b>Pousada:</b> {pousadaNome[c.pousada_id] || c.pousada_id}</div>}
                  <div><b>Saldo inicial:</b> {brl(c.saldo_inicial)}</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => abrirDetalhe(c)}
                    data-testid={`btn-cf-detalhe-${c.id}`}>
                    <Eye size={14} /> Detalhes
                  </Button>
                  {podeEscrever && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => abrirEdicao(c)}
                        data-testid={`btn-cf-editar-${c.id}`}>
                        <PencilLine size={14} /> Editar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => confirmarToggle(c)}
                        data-testid={`btn-cf-toggle-${c.id}`}>
                        {c.ativo ? <><PowerOff size={14} /> Desativar</> : <><Power size={14} /> Ativar</>}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: tabela */}
          <div className="hidden md:block bg-[#FFFFFF] border border-stone-200 rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-stone-50 hover:bg-stone-50">
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Nome</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Tipo</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Banco</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Conta</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Pousada</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Saldo inicial</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {itensFiltradosBusca.map((c) => (
                  <TableRow key={c.id} data-testid={`cf-row-${c.id}`}>
                    <TableCell className="font-medium text-stone-900">{c.nome}</TableCell>
                    <TableCell><TipoTag tipo={c.tipo} /></TableCell>
                    <TableCell className="text-stone-700 text-sm">{c.banco || "—"}</TableCell>
                    <TableCell className="text-stone-700 text-sm">
                      {c.conta ? `${c.conta}${c.digito_conta ? `-${c.digito_conta}` : ""}` : "—"}
                    </TableCell>
                    <TableCell className="text-stone-700 text-sm">
                      {c.pousada_id ? (pousadaNome[c.pousada_id] || c.pousada_id) : <span className="text-stone-400">Corporativo</span>}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{brl(c.saldo_inicial)}</TableCell>
                    <TableCell><StatusBadge ativo={c.ativo} /></TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => abrirDetalhe(c)}
                          aria-label={`Ver detalhes de ${c.nome}`} data-testid={`btn-cf-detalhe-${c.id}`}>
                          <Eye size={14} />
                        </Button>
                        {podeEscrever && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => abrirEdicao(c)}
                              aria-label={`Editar ${c.nome}`} data-testid={`btn-cf-editar-${c.id}`}>
                              <PencilLine size={14} />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => confirmarToggle(c)}
                              aria-label={`${c.ativo ? "Desativar" : "Ativar"} ${c.nome}`}
                              data-testid={`btn-cf-toggle-${c.id}`}>
                              {c.ativo ? <PowerOff size={14} /> : <Power size={14} />}
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      {/* Detalhes */}
      <Dialog open={detalheOpen} onOpenChange={(v) => { setDetalheOpen(v); if (!v) setDetalheAlvo(null); }}>
        <DialogContent className="max-w-xl" data-testid="dialog-cf-detalhe">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 size={18} className="text-[#083A6B]" />
              {detalheAlvo?.nome || "Detalhes"}
            </DialogTitle>
            <DialogDescription>
              Dados sensíveis podem estar mascarados conforme seu perfil de acesso.
            </DialogDescription>
          </DialogHeader>
          {detalheLoading ? (
            <div className="text-center text-stone-500 py-6">Carregando...</div>
          ) : detalheAlvo ? (
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <div><div className="text-[11px] uppercase text-stone-500">Tipo</div><TipoTag tipo={detalheAlvo.tipo} /></div>
              <div><div className="text-[11px] uppercase text-stone-500">Status</div><StatusBadge ativo={detalheAlvo.ativo} /></div>
              <div><div className="text-[11px] uppercase text-stone-500">Banco</div>{detalheAlvo.banco || "—"}</div>
              <div><div className="text-[11px] uppercase text-stone-500">Agência</div>{detalheAlvo.agencia || "—"}</div>
              <div><div className="text-[11px] uppercase text-stone-500">Conta</div>
                {detalheAlvo.conta ? `${detalheAlvo.conta}${detalheAlvo.digito_conta ? `-${detalheAlvo.digito_conta}` : ""}` : "—"}
              </div>
              <div><div className="text-[11px] uppercase text-stone-500">Titular</div>{detalheAlvo.titular || "—"}</div>
              <div><div className="text-[11px] uppercase text-stone-500">Documento</div>{detalheAlvo.documento_titular || "—"}</div>
              <div><div className="text-[11px] uppercase text-stone-500">Pousada</div>
                {detalheAlvo.pousada_id ? (pousadaNome[detalheAlvo.pousada_id] || detalheAlvo.pousada_id) : "Corporativo"}
              </div>
              <div><div className="text-[11px] uppercase text-stone-500">Saldo inicial</div>{brl(detalheAlvo.saldo_inicial)}</div>
              <div><div className="text-[11px] uppercase text-stone-500">Moeda</div>{detalheAlvo.moeda || "BRL"}</div>
              <div className="col-span-2"><div className="text-[11px] uppercase text-stone-500">Observações</div>{detalheAlvo.observacoes || "—"}</div>
              <div className="col-span-2 text-[11px] text-stone-500 pt-2 border-t border-stone-200">
                Criada em {detalheAlvo.created_at?.slice(0, 10) || "—"} · Atualizada em {detalheAlvo.updated_at?.slice(0, 10) || "—"}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Confirmação ativar/desativar */}
      <Dialog open={!!confirmAlvo} onOpenChange={(v) => { if (!v) setConfirmAlvo(null); }}>
        <DialogContent className="max-w-md" data-testid="dialog-cf-confirmar-toggle">
          <DialogHeader>
            <DialogTitle>
              {confirmAlvo?.ativo ? "Desativar esta conta financeira?" : "Ativar esta conta financeira?"}
            </DialogTitle>
            <DialogDescription>
              {confirmAlvo?.ativo
                ? "Ela permanecerá registrada no sistema e poderá ser reativada posteriormente."
                : "A conta voltará a aparecer disponível para os módulos financeiros."}
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm text-stone-700">
            <b>{confirmAlvo?.nome}</b>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmAlvo(null)} disabled={toggling}>Cancelar</Button>
            <Button data-testid="btn-confirmar-toggle" onClick={executarToggle} disabled={toggling}>
              {toggling ? "Processando..." : confirmAlvo?.ativo ? "Desativar" : "Ativar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
